import { useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Users } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import StatusPill from '../../components/StatusPill'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { formatDateTime } from '../../utils'

import SectionTitle from './SectionTitle'
import Detail from './Detail'
import { useVisitorDetails } from './useVisitorDetails'
import VisitorLoader from './VisitorLoader'
import Permission from './Permission'

const GuestDetailsModal = ({ guestId, onClose, selectedBuilding }) => {
  const { t } = useTranslation(['visitors', 'common'])
  const { data, isLoading, isError } = useVisitorDetails('guest', guestId)
  const building = useSelector(selectActiveBuilding)
  const brivoAvailable = building?.brivoAvailable
  const brivoVisitorAccessAvailable = building?.brivoVisitorAccessAvailable
  const swiftlaneAvailable = building?.swiftlaneAvailable
  const swiftlaneVisitorAccessAvailable = building?.swiftlaneVisitorAccessAvailable
  const butterflyVisitorAccessAvailable = building?.butterflyVisitorAccessAvailable
  const saltoSpaceAvailable = building?.saltoSpaceAvailable
  const saltoSpacePinVisitorAccessAvailable = building?.saltoSpacePinVisitorAccessAvailable
  const physicalKeyAvailable = building?.physicalKeyAvailable
  const customAccessAvailable = building?.customAccessAvailable
  const showPinCodeAccess =
    (swiftlaneAvailable && swiftlaneVisitorAccessAvailable) ||
    (brivoAvailable && brivoVisitorAccessAvailable) ||
    butterflyVisitorAccessAvailable ||
    (saltoSpaceAvailable && saltoSpacePinVisitorAccessAvailable)
  const saltoSpaceVisitorAccessAvailable = building?.saltoSpaceVisitorAccessAvailable
  const remoteLockAvailable = building?.remoteLockAvailable
  const remoteLockVisitorAccessAvailable = building?.remoteLockVisitorAccessAvailable
  const iglooWorkAvailable = building?.iglooWorkAvailable
  const iglooWorkVisitorAccessAvailable = building?.iglooWorkVisitorAccessAvailable
  const showKeylessAccess =
    (saltoSpaceAvailable && saltoSpaceVisitorAccessAvailable) ||
    (remoteLockAvailable && remoteLockVisitorAccessAvailable) ||
    (iglooWorkAvailable && iglooWorkVisitorAccessAvailable)

  const invitationEmailSentAt = data?.visitor?.invitationEmailSentAt
  const invitationEmail = formatDateTime(invitationEmailSentAt, selectedBuilding?.timezone)
  const invitationEmailStatus = invitationEmailSentAt
    ? t('visitors:detailsModal.invitationSent', {
        date: invitationEmail.date,
        time: invitationEmail.time,
      })
    : t('visitors:detailsModal.invitationNotSent')

  useEffect(() => {
    if (isError) {
      onClose()
    }
  }, [isError])

  if (!guestId) {
    return null
  }

  return (
    <ModalContainer
      additionalClass="visitor-modal"
      onClose={onClose}
      headerTitle={t('visitors:detailsModal.guestVendorTitle', {
        type: t('visitors:modalTypes.guest'),
      })}
      headerIcon={<Users size={20} />}
    >
      {isLoading || !data ? (
        <VisitorLoader />
      ) : (
        <div className="visitor-details">
          {data.visitor.notes && (
            <div className="visitor-details__section">
              <SectionTitle title={t('visitors:detailsModal.note')} />
              <Detail
                label={t('visitors:detailsModal.note')}
                className="visitor-details__section-item--vertical"
                value={data.visitor.notes}
              />
            </div>
          )}

          <div className="visitor-details__section">
            <SectionTitle title={t('visitors:detailsModal.guestInformation')} />
            <div className="visitor-details__section-items-container">
              <Detail label={t('visitors:detailsModal.guestName')} value={data.visitor.name} />
              <Detail label={t('visitors:detailsModal.guestStatus')}>
                <StatusPill status={data.visitor.status} />
              </Detail>
              <Detail label={t('visitors:detailsModal.email')} value={data.visitor.email} />
              <Detail label={t('visitors:detailsModal.phone')} value={data.visitor.phone} />
              <Detail label={t('visitors:detailsModal.company')} value={data.visitor.company} />
              <Detail label={t('visitors:detailsModal.guestType')} value={data.visitor.type} />
              {data.visitor.status === 'banned' && data.visitor.bannedReason && (
                <Detail
                  label={t('visitors:detailsModal.banReason')}
                  className="visitor-details__section-item--vertical"
                  value={data.visitor.bannedReason}
                />
              )}
            </div>
          </div>

          <div className="visitor-details__section">
            <SectionTitle title={t('visitors:detailsModal.permissionAndVisitDetails')} />
            <Detail label={t('visitors:detailsModal.permissionType')} value={data.permissionType}>
              <Permission visitor={data.visitor} timezone={selectedBuilding.timezone} />
            </Detail>
            <Detail
              label={t('visitors:detailsModal.startTime')}
              value={formatDateTime(data.visitor.startTime, selectedBuilding.timezone).time}
            />
            <Detail
              label={t('visitors:detailsModal.endTime')}
              value={formatDateTime(data.visitor.endTime, selectedBuilding.timezone).time}
            />
            <Detail label={t('visitors:detailsModal.createdBy')} value={data.visitor.createdBy} />
            <Detail
              label={t('visitors:detailsModal.permissionToEnter')}
              value={
                data.visitor.permissionToEnter ? t('common:booleans.yes') : t('common:booleans.no')
              }
            />
            {customAccessAvailable && (
              <Detail
                label={building.customAccessName}
                value={
                  data.visitor.customAccess ? t('common:booleans.yes') : t('common:booleans.no')
                }
              />
            )}
            {physicalKeyAvailable && (
              <Detail
                label={t('visitors:detailsModal.physicalKeyRelease')}
                value={
                  data.visitor.keylessAccess ? t('common:booleans.yes') : t('common:booleans.no')
                }
              />
            )}
            {showKeylessAccess && (
              <Detail
                label={t('visitors:detailsModal.keylessAccess')}
                value={data.visitor.keyAccess ? t('common:booleans.yes') : t('common:booleans.no')}
              />
            )}
            {showKeylessAccess && data.visitor.keyAccess && (
              <Detail
                label={t('visitors:detailsModal.invitationEmail')}
                value={invitationEmailStatus}
              />
            )}
            {showPinCodeAccess && (
              <Detail
                label={t('visitors:detailsModal.pinCodeAccess')}
                value={data.visitor.pinCode ? t('common:booleans.yes') : t('common:booleans.no')}
              />
            )}
            <Detail
              label={t('visitors:detailsModal.guestId')}
              value={data.visitor.guestId || data.visitor.vendorId}
            />
          </div>

          <div className="visitor-details__section">
            <SectionTitle title={t('visitors:detailsModal.residentDetails')} />
            <Detail
              label={t('visitors:detailsModal.residentName')}
              value={data.visitor.residentName}
            />
            <Detail
              label={t('visitors:detailsModal.residentUnitNumber')}
              value={data.visitor.unitNumber}
            />
            <Detail
              label={t('visitors:detailsModal.residentPhone')}
              value={data.visitor.residentPhone}
            />
          </div>

          {data.checkIns.length ? (
            <div className="visitor-details__section">
              <SectionTitle title={t('visitors:detailsModal.checkInLog')} />
              {data.checkIns.map((log, index) => {
                const date = formatDateTime(log.date, selectedBuilding.timezone)
                return <Detail key={index} label={date.date} value={date.time} />
              })}
            </div>
          ) : null}
        </div>
      )}
    </ModalContainer>
  )
}

GuestDetailsModal.propTypes = {
  guestId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  selectedBuilding: PropTypes.object,
}

export default connect((state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
})(GuestDetailsModal)
